import { transactionTime } from '../../helpers/transactionTrace';
import { shiftFinancialTotals } from "../../helpers/shiftFinancialTotals";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowLeft, FaCashRegister, FaCheckCircle, FaCreditCard, FaMoneyBillWave, FaPrint, FaReceipt, FaUndoAlt, FaUserClock } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Badge, Button, Card, CardBody, Col, Container, Input, Row, Spinner } from "reactstrap";
import jsPDF from "jspdf";
import { shiftActions } from "../../sagas/shiftSlice";
import { deliveryActions } from "../../sagas/deliveryNoteSlice";
import { customerReturnActions } from "../../sagas/customerReturnSlice";
import { formatPrice } from "../../helpers/utility";
import { printPdfInHiddenIframe } from "../../helpers/printFrame";
import DocumentPagination from "../Common/DocumentPagination";
import Swal from "sweetalert2";
import "./ShiftPage.css";

const toNumber = (value) => Number(value || 0);
const getCashAmount = (bl) => toNumber(bl?.montantKeptCash ?? bl?.montantPayeCash ?? bl?.montantPayeEspece);
const getCardAmount = (bl) => toNumber(bl?.montantPayeCarte);
const getReturnCashAmount = (returnItem) => toNumber(returnItem?.montantPayeCash);
const getReturnCardAmount = (returnItem) => toNumber(returnItem?.montantPayeCarte);
const isNumericSearch = (value) => {
  if (!value) return false;
  return !Number.isNaN(Number(String(value).replace(",", ".")));
};
const togglePaymentFilter = (currentFilter, nextFilter) => currentFilter === nextFilter ? "all" : nextFilter;
const amountSortLabels = {
  total: "TTC",
  paid: "Paye",
  cash: "Especes",
  card: "Carte",
};
const returnAmountSortLabels = {
  total: "TTC",
  refunded: "Rembourse",
  cash: "Especes",
  card: "Carte",
};
const toggleAmountSort = (setSort, field) => {
  setSort((sort) => ({
    field,
    direction: sort.field === field && sort.direction === "desc" ? "asc" : "desc",
  }));
};
const getSortMark = (sort, field) => {
  if (sort.field !== field) return "";
  return sort.direction === "asc" ? " ^" : " v";
};
const getModeReglementLabel = (mode) => {
  switch (mode) {
    case "ESPECES":
      return "Especes";
    case "CARTE_BANCAIRE":
      return "Carte bancaire";
    case "CHEQUE":
      return "Cheque";
    case "VIREMENT":
      return "Virement";
    case "TRAITE":
      return "Traite";
    case "EN_COMPTE":
      return "En compte";
    default:
      return mode || "-";
  }
};
const getReceiptPaymentSummary = (data) => {
  const total = Number(data?.montantTotalTTC || 0);
  const cash = Number(data?.montantKeptCash ?? data?.montantPayeCash ?? data?.montantPayeEspece ?? 0);
  const card = Number(data?.montantPayeCarte || 0);
  const stampDuty = Number(data?.montantDroitTimbre || 0);
  const totalDue = total + stampDuty;
  const explicitPaid = Number(data?.montantPaye || 0);
  const computedPaid = cash + card;
  const paid = explicitPaid > 0 ? Math.max(explicitPaid, computedPaid) : computedPaid;
  const change = Math.max(paid - totalDue, 0);
  return { cash, card, paid, change, stampDuty, totalDue };
};

const ShiftDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const location = useLocation();
  const editMode = location.state?.editMode;
  const shift = location.state?.shift;

  const { summary, loading } = useSelector((state) => state.shift);
  const {
    list = [],
    loading: deliveryLoading,
    totalPages,
    pageNumber,
    pageSize,
    totalElements,
  } = useSelector((state) => state.deliveryNotes);
  const {
    customerReturns = [],
    loading: customerReturnsLoading,
  } = useSelector((state) => state.customerReturns);
  const { businessName, address, telephone } = useSelector((state) => state.auth);

  const [closingCash, setClosingCash] = useState(0);
  const [closingCard, setClosingCard] = useState(0);
  const [salesFilters, setSalesFilters] = useState({ page: 0, size: 10 });
  const [returnsFilters, setReturnsFilters] = useState({ page: 0, size: 10 });
  const [salesSearch, setSalesSearch] = useState("");
  const [returnsSearch, setReturnsSearch] = useState("");
  const [salesPaymentFilter, setSalesPaymentFilter] = useState("all");
  const [returnsPaymentFilter, setReturnsPaymentFilter] = useState("all");
  const [salesSort, setSalesSort] = useState({ field: null, direction: "desc" });
  const [returnsSort, setReturnsSort] = useState({ field: null, direction: "desc" });

  const isValidated = summary?.status === "VALIDATED";
  const isClosed = summary?.status === "CLOSED";

  useEffect(() => {
    if (!shift?.id) return;
    dispatch(shiftActions.fetchSummary({ shiftId: shift.id }));
  }, [shift?.id, dispatch]);

  useEffect(() => {
    const search = salesSearch.trim();
    setSalesFilters((filters) => ({
      ...filters,
      page: 0,
      query: search && !isNumericSearch(search) ? search : undefined,
      amount: isNumericSearch(search) ? search.replace(",", ".") : undefined,
      paymentFilter: salesPaymentFilter !== "all" ? salesPaymentFilter : undefined,
      sortField: salesSort.field || undefined,
      sortDirection: salesSort.field ? salesSort.direction : undefined,
    }));
  }, [salesSearch, salesPaymentFilter, salesSort]);

  useEffect(() => {
    setSalesFilters((filters) => filters.page === 0 ? filters : { ...filters, page: 0 });
    setReturnsFilters((filters) => filters.page === 0 ? filters : { ...filters, page: 0 });
  }, [shift?.id]);

  useEffect(() => {
    setReturnsFilters((filters) => filters.page === 0 ? filters : { ...filters, page: 0 });
  }, [returnsSearch, returnsPaymentFilter, returnsSort]);

  useEffect(() => {
    if (!shift?.id) return;
    dispatch(deliveryActions.list({ shiftId: shift.id, ...salesFilters }));
  }, [shift?.id, salesFilters, dispatch]);

  useEffect(() => {
    if (!shift?.id) return;
    const search = returnsSearch.trim();
    dispatch(customerReturnActions.list({
      shiftId: shift.id,
      query: search && !isNumericSearch(search) ? search : undefined,
      amount: isNumericSearch(search) ? search.replace(",", ".") : undefined,
      paymentFilter: returnsPaymentFilter !== "all" ? returnsPaymentFilter : undefined,
      sortField: returnsSort.field || undefined,
      sortDirection: returnsSort.field ? returnsSort.direction : undefined,
    }));
  }, [shift?.id, returnsSearch, returnsPaymentFilter, returnsSort, dispatch]);

  useEffect(() => {
    if (!summary) return;
    setClosingCash(summary.closingAmountCash || 0);
    setClosingCard(summary.closingAmountCard || 0);
  }, [summary]);

  const cashDifference = useMemo(
    () => Number(closingCash || 0) - Number(summary?.expectedCashAmount || 0),
    [closingCash, summary]
  );
  const cardDifference = useMemo(
    () => Number(closingCard || 0) - Number(summary?.expectedCardAmount || 0),
    [closingCard, summary]
  );

  const financial = shiftFinancialTotals(summary || {});
  const returnsWithTicket = financial.ticketReturns;
  const returnsWithoutTicket = financial.noTicketReturns;
  const expectedTotal = Number(summary?.expectedCashAmount || 0) + Number(summary?.expectedCardAmount || 0);
  const closingTotal = Number(closingCash || 0) + Number(closingCard || 0);

  const returnTotals = { totalTTC: financial.returns, cash: financial.cashRefunds, card: financial.cardRefunds };

  const salesRows = list;
  const filteredReturns = customerReturns;

  const paginatedReturns = useMemo(() => {
    const start = returnsFilters.page * returnsFilters.size;
    return filteredReturns.slice(start, start + returnsFilters.size);
  }, [filteredReturns, returnsFilters.page, returnsFilters.size]);

  const returnTotalPages = Math.ceil(filteredReturns.length / returnsFilters.size);

  useEffect(() => {
    if (!returnTotalPages || returnsFilters.page < returnTotalPages) return;
    setReturnsFilters((filters) => ({ ...filters, page: Math.max(returnTotalPages - 1, 0) }));
  }, [returnTotalPages, returnsFilters.page]);

  const formatDateTime = (value) => {
    return transactionTime(value);
  };

  const generateThermalReceipt = async (ticketData) => {
    try {
      const measureDoc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [80, 350],
      });
      const estimatedAddressHeight = address
        ? measureDoc.splitTextToSize(address, 72).slice(0, 3).length * 3.3
        : 0;
      const estimatedItemsHeight = (ticketData?.lignes || []).reduce((height, item) => {
        const nameLines = measureDoc.splitTextToSize(item.articleNom || "-", 38);
        return height + 3.8 + Math.max(nameLines.length - 1, 0) * 3.4;
      }, 0);
      const payment = getReceiptPaymentSummary(ticketData);
      const receiptHeight = Math.ceil(
        265 +
        estimatedAddressHeight +
        (telephone ? 3.5 : 0) +
        estimatedItemsHeight +
        (payment.stampDuty > 0 ? 4.2 : 0) +
        (payment.change > 0 ? 4.2 : 0)
      );
      const thermalPageHeight = Math.min(Math.max(receiptHeight, 230), 280);
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [80, thermalPageHeight],
      });

      const pageWidth = 80;
      const margin = 4;
      const contentWidth = pageWidth - margin * 2;
      const right = pageWidth - margin;
      let y = 7;
      const pageHeight = doc.internal.pageSize.getHeight();
      const bottomMargin = 8;

      const ensureSpace = (height = 0) => {
        if (y + height <= pageHeight - bottomMargin) return;
        doc.addPage([pageWidth, pageHeight], "portrait");
        y = 7;
      };

      const drawRule = (bold = false) => {
        ensureSpace(bold ? 4 : 3.5);
        doc.setDrawColor(0);
        doc.setLineWidth(bold ? 0.35 : 0.12);
        doc.line(margin, y, right, y);
        y += bold ? 3.6 : 3;
      };

      const drawRow = (label, value, { bold = false, size = 9.5 } = {}) => {
        ensureSpace(size >= 11 ? 5.5 : 4.7);
        doc.setFont("helvetica", bold ? "bold" : "normal");
        doc.setFontSize(size);
        doc.text(label, margin, y);
        doc.text(formatPrice(value, false), right, y, { align: "right" });
        y += size >= 11 ? 5 : 4.2;
      };

      const drawInfo = (label, value) => {
        ensureSpace(4.2);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.2);
        doc.text(`${label}:`, margin, y);
        doc.text(String(value || "-"), margin + 15, y, { maxWidth: contentWidth - 15 });
        y += 3.8;
      };

      const totalQuantity = (ticketData?.lignes || []).reduce(
        (sum, item) => sum + Number(item.quantiteLivree || 0),
        0
      );

      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.text(businessName || "MATJARPOS", pageWidth / 2, y, { align: "center" });
      y += 5.5;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      if (address) {
        doc.splitTextToSize(address, contentWidth).slice(0, 3).forEach((line) => {
          doc.text(line, pageWidth / 2, y, { align: "center" });
          y += 3.3;
        });
      }
      if (telephone) {
        doc.text(`Tel: ${telephone}`, pageWidth / 2, y, { align: "center" });
        y += 3.5;
      }

      drawRule(true);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("TICKET DE CAISSE", pageWidth / 2, y, { align: "center" });
      y += 5;

      drawInfo("Ticket", ticketData?.numero);
      drawInfo("Date", ticketData?.createdAt || ticketData?.dateOperation);
      drawInfo("Client", ticketData?.clientNom || "Passager");
      drawRule();

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.text("DESIGNATION", margin, y);
      doc.text("QTE", 47, y, { align: "right" });
      doc.text("P.U.", 59, y, { align: "right" });
      doc.text("TOTAL", right, y, { align: "right" });
      y += 3.5;
      drawRule();

      (ticketData?.lignes || []).forEach((item) => {
        const qte = Number(item.quantiteLivree || 0);
        const pu = Number(item.prixUnitaireTTC || 0);
        const total = Number(item.montantTTC || qte * pu);
        const nameLines = doc.splitTextToSize(item.articleNom || "-", 38);
        ensureSpace(3.8 + Math.max(nameLines.length - 1, 0) * 3.4);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.text(nameLines[0], margin, y);
        doc.text(String(qte), 47, y, { align: "right" });
        doc.text(formatPrice(pu, false), 59, y, { align: "right" });
        doc.text(formatPrice(total, false), right, y, { align: "right" });
        y += 3.8;

        if (nameLines.length > 1) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          nameLines.slice(1).forEach((line) => {
            doc.text(line, margin + 2, y);
            y += 3.4;
          });
        }
      });

      ensureSpace(90);
      drawRule();
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.text(`${ticketData?.lignes?.length || 0} article(s)`, margin, y);
      doc.text(`${totalQuantity} piece(s)`, right, y, { align: "right" });
      y += 4.2;
      drawRule(true);

      drawRow("TOTAL TTC", ticketData?.montantTotalTTC || 0, { bold: true, size: 11 });
      if (payment.stampDuty > 0) {
        drawRow("Droit de timbre 0,25%", payment.stampDuty);
      }

      ensureSpace(10);
      doc.setFillColor(0, 0, 0);
      doc.rect(margin, y - 0.5, contentWidth, 7, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("NET A PAYER", margin + 2, y + 4.2);
      doc.text(formatPrice(payment.totalDue, false), right - 2, y + 4.2, { align: "right" });
      doc.setTextColor(0, 0, 0);
      y += 9.5;

      ensureSpace(34);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.text("PAIEMENT", margin, y);
      y += 4.2;
      drawInfo("Mode", getModeReglementLabel(ticketData?.modeReglement));
      drawRow("Especes", payment.cash);
      drawRow("Carte", payment.card);
      drawRow("Montant paye", payment.paid, { bold: true });
      if (payment.change > 0) {
        drawRow("Monnaie", payment.change, { bold: true });
      }

      ensureSpace(18);
      drawRule();
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.text("Merci de votre confiance", pageWidth / 2, y, { align: "center" });
      y += 4;
      doc.text("A bientot", pageWidth / 2, y, { align: "center" });
      y += 4;
      doc.text("cima.ma / @cimamarket.ma", pageWidth / 2, y, { align: "center" });

	      printPdfInHiddenIframe(doc.output("bloburl"));
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: t("sale.error"),
        text: "Impossible d'imprimer le ticket.",
      });
    }
  };

  const statusBadge = () => {
    if (isValidated) return <Badge color="success" pill>{t("shifts.validated")}</Badge>;
    if (isClosed) return <Badge color="danger" pill>{t("shifts.closed")}</Badge>;
    return <Badge color="warning" pill>{t("shifts.opened")}</Badge>;
  };

  const diffColor = (value) => {
    if (Number(value) === 0) return "success";
    return Number(value) > 0 ? "warning" : "danger";
  };

  const handleValidate = () => {
    Swal.fire({
      title: "Valider la clôture ?",
      text: "Cette action est définitive. Voulez-vous continuer ?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Oui, valider",
      cancelButtonText: "Annuler",
      reverseButtons: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
    }).then((result) => {
      if (!result.isConfirmed) return;
      dispatch(
        shiftActions.validateShift({
          id: summary.shiftId,
          data: {
            isValidated: true,
            closingAmountCash: closingCash,
            closingAmountCard: closingCard,
          },
          onSuccess: () => {
            Swal.fire({
              icon: "success",
              title: t("common.updated"),
              text: t("shifts.updatedSuccess"),
            });
            navigate(-1);
          },
        })
      );
    });
  };

  const MetricCard = ({ icon, label, value, tone = "" }) => (
    <div className="shift-kpi">
      <span className={tone}>{icon}</span>
      <span>{label}</span>
      <strong>{formatPrice(value)}</strong>
    </div>
  );

  if (!summary) {
    return (
      <Container fluid className="shift-page">
        <div className="shift-empty">
          <Spinner color="primary" />
          <div>{t("common.loading")}</div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="shift-page">
      <div className="shift-header">
        <div>
          <div className="shift-title">
            <FaUserClock />
            {t("shifts.details")} {shift?.shiftCode || summary.shiftCode}
          </div>
          <div className="shift-subtitle">
            {summary.commercialName} · {formatDateTime(summary.openingTime)} → {formatDateTime(summary.closingTime)}
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          {statusBadge()}
          <Button color="light" className="shift-action-btn" onClick={() => navigate(-1)}>
            <FaArrowLeft />
          </Button>
        </div>
      </div>

      <Row className="g-3 mb-3">
        <Col lg={4} md={6}>
          <MetricCard icon={<FaCashRegister />} label="Ventes nettes après retours" value={financial.sales} tone="text-primary" />
        </Col>
        <Col lg={4} md={6}>
          <MetricCard icon={<FaUndoAlt />} label="Retours avec ticket" value={returnsWithTicket} tone="text-warning" />
        </Col>
        <Col lg={4} md={6}>
          <MetricCard icon={<FaUndoAlt />} label="Retours sans ticket" value={returnsWithoutTicket} tone="text-danger" />
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col lg={6}>
          <Card className="shift-list-card h-100">
            <CardBody>
              <div className="shift-detail-section-title">
                <FaMoneyBillWave />
                Espèces
              </div>
              <div className="shift-payment-grid">
                <div>
                  <span>Encaissements bruts</span>
                  <strong>{formatPrice(summary.totalCashPayments)}</strong>
                </div>
                <div>
                  <span>Remboursements</span>
                  <strong>{formatPrice(financial.cashRefunds)}</strong>
                </div>
                <div>
                  <span>Attendu</span>
                  <strong>{formatPrice(summary.expectedCashAmount)}</strong>
                </div>
              </div>
              <div className="shift-close-edit">
                <Input
                  type="number"
                  disabled={!editMode || !isClosed || isValidated}
                  value={closingCash}
                  onChange={(e) => setClosingCash(e.target.value)}
                />
                <Badge color={diffColor(cashDifference)}>{formatPrice(cashDifference)}</Badge>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="shift-list-card h-100">
            <CardBody>
              <div className="shift-detail-section-title">
                <FaCreditCard />
                Carte bancaire
              </div>
              <div className="shift-payment-grid">
                <div>
                  <span>Encaissements bruts</span>
                  <strong>{formatPrice(summary.totalCardPayments)}</strong>
                </div>
                <div>
                  <span>Remboursements</span>
                  <strong>{formatPrice(financial.cardRefunds)}</strong>
                </div>
                <div>
                  <span>Attendu</span>
                  <strong>{formatPrice(summary.expectedCardAmount)}</strong>
                </div>
              </div>
              <div className="shift-close-edit">
                <Input
                  type="number"
                  disabled={!editMode || !isClosed || isValidated}
                  value={closingCard}
                  onChange={(e) => setClosingCard(e.target.value)}
                />
                <Badge color={diffColor(cardDifference)}>{formatPrice(cardDifference)}</Badge>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Card className="shift-list-card mb-3">
        <CardBody>
          <div className="shift-detail-section-title">
            <FaCheckCircle />
            Clôture
          </div>
          <Row className="g-3">
            <Col md={4}>
              <div className="shift-total-box">
                <span>Total attendu</span>
                <strong>{formatPrice(expectedTotal)}</strong>
              </div>
            </Col>
            <Col md={4}>
              <div className="shift-total-box">
                <span>Total clôturé</span>
                <strong>{formatPrice(closingTotal)}</strong>
              </div>
            </Col>
            <Col md={4}>
              <div className="shift-total-box">
                <span>Écart total</span>
                <Badge color={diffColor(closingTotal - expectedTotal)}>{formatPrice(closingTotal - expectedTotal)}</Badge>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {editMode && isClosed && !isValidated && (
        <div className="d-flex justify-content-end mb-3">
          <Button color="success" onClick={handleValidate} disabled={loading} className="fw-bold">
            {loading && <Spinner size="sm" className="me-2" />}
            {t("common.validate")}
          </Button>
        </div>
      )}

      <Card className="shift-list-card">
        <CardBody className="p-0">
          <div className="shift-sales-header px-3 pt-3">
            <div className="shift-detail-section-title mb-0">
              <FaReceipt />
              Ventes du shift
            </div>
            <DocumentPagination
              pageNumber={pageNumber}
              totalPages={totalPages}
              totalElements={totalElements}
              pageSize={pageSize}
              onPageChange={(page) => setSalesFilters((filters) => ({ ...filters, page }))}
            />
          </div>
          <div className="shift-sales-totals">
            <button
              type="button"
              className={`shift-total-filter ${salesPaymentFilter === "all" ? "active" : ""}`}
              onClick={() => setSalesPaymentFilter("all")}
            >
              <span>Ventes nettes</span>
              <strong>{formatPrice(financial.sales)}</strong>
            </button>
            <button
              type="button"
              className={`shift-total-filter ${salesPaymentFilter === "cash" ? "active" : ""}`}
              onClick={() => setSalesPaymentFilter((filter) => togglePaymentFilter(filter, "cash"))}
            >
              <span>Espèces nettes</span>
              <strong>{formatPrice(financial.cash)}</strong>
            </button>
            <button
              type="button"
              className={`shift-total-filter ${salesPaymentFilter === "card" ? "active" : ""}`}
              onClick={() => setSalesPaymentFilter((filter) => togglePaymentFilter(filter, "card"))}
            >
              <span>Carte nette</span>
              <strong>{formatPrice(financial.card)}</strong>
            </button>
            <button type="button" className="shift-total-filter" onClick={() => setSalesPaymentFilter("all")}>
              <span>Ventes affichees</span>
              <strong>{salesRows.length} / {totalElements || 0}</strong>
            </button>
          </div>
          <p className="small text-muted px-3 mb-2">Les tickets ci-dessous conservent leurs montants d’origine. Les totaux du shift sont nets des retours.</p>
          <div className="shift-list-toolbar">
            <Input
              value={salesSearch}
              onChange={(e) => setSalesSearch(e.target.value)}
              placeholder="Rechercher par numero BL ou montant"
            />
            <div className="shift-sort-actions">
              {Object.entries(amountSortLabels).map(([field, label]) => (
                <Button
                  key={field}
                  color={salesSort.field === field ? "primary" : "light"}
                  size="sm"
                  onClick={() => toggleAmountSort(setSalesSort, field)}
                >
                  {label}{getSortMark(salesSort, field)}
                </Button>
              ))}
            </div>
          </div>
          {deliveryLoading ? (
            <div className="shift-empty">
              <Spinner color="primary" />
              <div>{t("common.loading")}</div>
            </div>
          ) : salesRows.length ? (
            <div className="shift-sales-list">
              {salesRows.map((bl) => (
                <div className="shift-sale-row" key={bl.id}>
                  <div>
                    <strong>{bl.numero}</strong>
                    <span>{bl.clientNom || "N/A"} · {transactionTime(bl.createdAt)}</span>
                  </div>
                  <div>
                    <span>TTC</span>
                    <strong>{formatPrice(bl.montantTotalTTC, false)} DH</strong>
                  </div>
                  <div>
                    <span>Paye</span>
                    <strong>{formatPrice(bl.montantPaye, false)} DH</strong>
                  </div>
                  <div>
                    <span>Especes</span>
                    <strong>{formatPrice(getCashAmount(bl), false)} DH</strong>
                  </div>
                  <div>
                    <span>Carte</span>
                    <strong>{formatPrice(getCardAmount(bl), false)} DH</strong>
                  </div>
	                  <Badge color={bl.status === "LIVRE" ? "success" : bl.status === "EN_PREPARATION" ? "warning" : "secondary"}>
	                    {bl.status || "-"}
	                  </Badge>
	                  <Button
	                    color="light"
	                    size="sm"
	                    className="shift-print-btn"
	                    title="Imprimer le ticket thermique"
	                    onClick={() => generateThermalReceipt(bl)}
	                  >
	                    <FaPrint />
	                  </Button>
	                </div>
	              ))}
            </div>
          ) : (
            <div className="shift-empty">{t("delivery.noBL")}</div>
          )}
        </CardBody>
      </Card>

      <Card className="shift-list-card">
        <CardBody className="p-0">
          <div className="shift-sales-header px-3 pt-3">
            <div className="shift-detail-section-title mb-0">
              <FaUndoAlt />
              Retours client du shift
            </div>
            <DocumentPagination
              pageNumber={returnsFilters.page}
              totalPages={returnTotalPages}
              totalElements={filteredReturns.length}
              pageSize={returnsFilters.size}
              onPageChange={(page) => setReturnsFilters((filters) => ({ ...filters, page }))}
            />
          </div>
          <div className="shift-sales-totals">
            <button
              type="button"
              className={`shift-total-filter ${returnsPaymentFilter === "all" ? "active" : ""}`}
              onClick={() => setReturnsPaymentFilter("all")}
            >
              <span>Shift retours</span>
              <strong>{formatPrice(returnTotals.totalTTC)}</strong>
            </button>
            <button
              type="button"
              className={`shift-total-filter ${returnsPaymentFilter === "cash" ? "active" : ""}`}
              onClick={() => setReturnsPaymentFilter((filter) => togglePaymentFilter(filter, "cash"))}
            >
              <span>Shift especes</span>
              <strong>{formatPrice(returnTotals.cash)}</strong>
            </button>
            <button
              type="button"
              className={`shift-total-filter ${returnsPaymentFilter === "card" ? "active" : ""}`}
              onClick={() => setReturnsPaymentFilter((filter) => togglePaymentFilter(filter, "card"))}
            >
              <span>Shift carte</span>
              <strong>{formatPrice(returnTotals.card)}</strong>
            </button>
            <button type="button" className="shift-total-filter" onClick={() => setReturnsPaymentFilter("all")}>
              <span>Retours affiches</span>
              <strong>{filteredReturns.length} / {customerReturns.length}</strong>
            </button>
          </div>
          <div className="shift-list-toolbar">
            <Input
              value={returnsSearch}
              onChange={(e) => setReturnsSearch(e.target.value)}
              placeholder="Rechercher par numero BL ou montant"
            />
            <div className="shift-sort-actions">
              {Object.entries(returnAmountSortLabels).map(([field, label]) => (
                <Button
                  key={field}
                  color={returnsSort.field === field ? "primary" : "light"}
                  size="sm"
                  onClick={() => toggleAmountSort(setReturnsSort, field)}
                >
                  {label}{getSortMark(returnsSort, field)}
                </Button>
              ))}
            </div>
          </div>
          {customerReturnsLoading ? (
            <div className="shift-empty">
              <Spinner color="primary" />
              <div>{t("common.loading")}</div>
            </div>
          ) : paginatedReturns.length ? (
            <div className="shift-sales-list">
              {paginatedReturns.map((returnItem) => (
                <div className="shift-sale-row" key={returnItem.id}>
                  <div>
                    <strong>{returnItem.numeroRetour}</strong>
                    <span>{returnItem.clientNom || "N/A"} - {returnItem.dateOperation || "N/A"}</span>
                    <span>BL: {returnItem.numBL || "-"}</span>
                  </div>
                  <div>
                    <span>TTC</span>
                    <strong>{formatPrice(returnItem.montantTTC, false)} DH</strong>
                  </div>
                  <div>
                    <span>Rembourse</span>
                    <strong>{formatPrice(returnItem.montantRembourse || returnItem.montantTTC, false)} DH</strong>
                  </div>
                  <div>
                    <span>Especes</span>
                    <strong>{formatPrice(getReturnCashAmount(returnItem), false)} DH</strong>
                  </div>
                  <div>
                    <span>Carte</span>
                    <strong>{formatPrice(getReturnCardAmount(returnItem), false)} DH</strong>
                  </div>
                  <Badge color={returnItem.statut === "VALIDE" ? "success" : returnItem.statut === "ANNULE" ? "danger" : "secondary"}>
                    {returnItem.statut || "-"}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="shift-empty">Aucun retour client pour ce shift</div>
          )}
        </CardBody>
      </Card>
    </Container>
  );
};

export default ShiftDetails;
